// ─── LensToggle ──────────────────────────────────────────────────────────────
//
// Accessible segmented control for switching between patient and clinician
// dashboard views. Implements the WAI-ARIA radiogroup pattern:
//   - container has role="radiogroup" and aria-label
//   - each option has role="radio" and aria-checked
//   - arrow keys (left/right/up/down) cycle selection
//   - the active radio gets tabindex=0; others get tabindex=-1 (roving tabindex)
//
// Visually this is a pill-shaped two-option toggle with the active side
// filled. It's small enough to sit inside the Primary Insight card header
// without introducing a whole new section.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useRef } from 'react';
import { useLens } from '../../lib/dashboard/lensContext';
import type { Lens } from '../../lib/dashboard/types';

interface LensToggleProps {
  /** Optional visual size. `sm` fits inside a card header; `md` stands alone. */
  size?: 'sm' | 'md';
  /** Optional className hook for layout parents. */
  className?: string;
}

const OPTIONS: { value: Lens; label: string; description: string }[] = [
  { value: 'patient', label: 'Patient', description: 'Supportive plain-language view' },
  { value: 'clinician', label: 'Clinician', description: 'Clinical interpretation view' },
];

export default function LensToggle({ size = 'sm', className = '' }: LensToggleProps) {
  const { lens, setLens } = useLens();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusIndex = useCallback((i: number) => {
    const btn = refs.current[i];
    if (btn) btn.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (index + 1) % OPTIONS.length;
        setLens(OPTIONS[next].value);
        focusIndex(next);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = (index - 1 + OPTIONS.length) % OPTIONS.length;
        setLens(OPTIONS[prev].value);
        focusIndex(prev);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setLens(OPTIONS[0].value);
        focusIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        const last = OPTIONS.length - 1;
        setLens(OPTIONS[last].value);
        focusIndex(last);
      }
    },
    [setLens, focusIndex],
  );

  const padding = size === 'sm' ? 'px-3 py-1' : 'px-4 py-1.5';
  const fontSize = size === 'sm' ? 'text-[11px]' : 'text-xs';

  return (
    <div
      role="radiogroup"
      aria-label="Dashboard view"
      className={`inline-flex items-center rounded-full border border-arete-sage/40 bg-white/60 dark:bg-white/5 p-0.5 ${className}`}
    >
      {OPTIONS.map((option, i) => {
        const isActive = option.value === lens;
        return (
          <button
            key={option.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={option.description}
            tabIndex={isActive ? 0 : -1}
            onClick={() => setLens(option.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            className={`
              ${padding} ${fontSize} rounded-full font-medium transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40
              ${isActive
                ? 'bg-arete-forest text-white shadow-sm dark:bg-arete-moss'
                : 'text-arete-muted hover:text-arete-forest dark:text-gray-400 dark:hover:text-gray-200'}
            `}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
